/**
 * Support FAB. In the app (canFileTicket) it offers "Report a problem /
 * feedback" — which opens the ticket dialog and captures the page the user is
 * on — plus a Teams-chat fallback. On the login screen (no auth) it's just the
 * Teams chat.
 */

import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  makeStyles,
  tokens,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Button,
  Text,
} from '@fluentui/react-components'
import { Chat20Regular } from '@fluentui/react-icons'
import { TEAMS_CHAT_URL, MAINTAINER_NAME } from '../lib/support'
import { useToast } from './ToastProvider'
import { NewTicketDialog } from './NewTicketDialog'

const useStyles = makeStyles({
  fab: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 9999,
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#6264a7',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: `0 2px 8px ${tokens.colorNeutralShadowAmbient}`,
    transitionProperty: 'transform, box-shadow',
    transitionDuration: '150ms',
    ':hover': {
      transform: 'scale(1.1)',
      backgroundColor: '#4f4fa0',
      boxShadow: `0 4px 16px ${tokens.colorNeutralShadowAmbient}`,
    },
  },
  popover: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
    maxWidth: '260px',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
})

export function TeamsChatFab({ canFileTicket = false }: { canFileTicket?: boolean }) {
  const styles = useStyles()
  const [open, setOpen] = useState(false)
  const [ticketOpen, setTicketOpen] = useState(false)
  const location = useLocation()
  const toast = useToast()

  return (
    <>
      <Popover positioning="above-end" open={open} onOpenChange={(_, data) => setOpen(data.open)}>
        <PopoverTrigger disableButtonEnhancement>
          <button className={styles.fab} aria-label="Help and feedback">
            <Chat20Regular />
          </button>
        </PopoverTrigger>

        <PopoverSurface className={styles.popover}>
          <Text weight="semibold">Need help?</Text>
          {canFileTicket && (
            <>
              <Text size={200}>
                Report a bug, request a feature, or ask a question — it’s tracked under Tickets.
              </Text>
              <Button
                size="small"
                appearance="primary"
                onClick={() => {
                  setOpen(false)
                  setTicketOpen(true)
                }}
                data-testid="fab-report"
              >
                Report a problem / feedback
              </Button>
            </>
          )}
          <Text size={200}>Or message {MAINTAINER_NAME} on Teams.</Text>
          <div className={styles.actions}>
            <Button size="small" appearance="subtle" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="small"
              appearance={canFileTicket ? 'secondary' : 'primary'}
              as="a"
              href={TEAMS_CHAT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
            >
              Open chat
            </Button>
          </div>
        </PopoverSurface>
      </Popover>

      {canFileTicket && (
        <NewTicketDialog
          open={ticketOpen}
          onClose={() => setTicketOpen(false)}
          contextPath={location.pathname + location.search}
          onCreated={() => {
            setTicketOpen(false)
            toast.showToast({ title: 'Ticket filed — track it under Tickets.' })
          }}
        />
      )}
    </>
  )
}
